import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { getPgFeeMatrixRequest, updatePgFeesRequest } from 'redux/reducers/pgSettings/reducer';
import {
	selectPgFeeMatrix,
	selectPgFeeMatrixLoading,
	selectPgSaveLoading,
} from 'redux/reducers/pgSettings/selectors';
import { PgPage } from '../common/PgPage';
import { PgPanel } from '../common/PgPanel';
import { LegalEntitySelect } from '../common/LegalEntitySelect';
import { usePgLegalEntities } from '../common/hooks';
import { PG_SETTINGS_GROUP } from '../constants';
import { buildChanges, buildDrafts, TFeeCellDraft, TFeeDrafts, validateCell } from './draft';
import { FeeMatrixTable } from './FeeMatrixTable';
import { FeeCalculator } from './FeeCalculator';

/**
 * Settings → Fees (decision 10, 03 §4.5): card / wallet / Direct Debit fee per pair
 * "service × payment method" of a legal entity. Saved with one `PUT /api/admin/fees` that carries
 * only the changed cells.
 */
export const Fees: FC = () => {
	const dispatch = useDispatch();
	const { legalEntities } = usePgLegalEntities();
	const matrix = useSelector(selectPgFeeMatrix);
	const loading = useSelector(selectPgFeeMatrixLoading);
	const saving = useSelector(selectPgSaveLoading('fees'));

	const [legalEntityId, setLegalEntityId] = useState<number | null>(null);
	const [drafts, setDrafts] = useState<TFeeDrafts>({});

	useEffect(() => {
		if (legalEntityId !== null) dispatch(getPgFeeMatrixRequest(legalEntityId));
	}, [dispatch, legalEntityId]);

	// The matrix of another legal entity may still be in the store from an earlier visit.
	const currentMatrix = matrix && matrix.legalEntityId === legalEntityId ? matrix : null;

	// Every new matrix (loaded or returned by a save) resets the drafts.
	const [syncedFrom, setSyncedFrom] = useState<typeof currentMatrix | undefined>(undefined);
	if (currentMatrix !== syncedFrom) {
		setSyncedFrom(currentMatrix);
		setDrafts(currentMatrix ? buildDrafts(currentMatrix) : {});
	}

	const handleCellChange = useCallback((key: string, patch: Partial<TFeeCellDraft>) => {
		setDrafts((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
	}, []);

	const changes = useMemo(() => buildChanges(drafts), [drafts]);
	const hasErrors = useMemo(
		() => Object.values(drafts).some((cell) => validateCell(cell) !== null),
		[drafts],
	);

	const handleSave = () => {
		if (!currentMatrix || !changes.length || hasErrors) return;
		dispatch(updatePgFeesRequest({ legalEntityId: currentMatrix.legalEntityId, rules: changes }));
	};

	const handleDiscard = () => {
		if (currentMatrix) setDrafts(buildDrafts(currentMatrix));
	};

	return (
		<PgPage
			title="Fees"
			group={PG_SETTINGS_GROUP}
			actions={
				<LegalEntitySelect
					legalEntities={legalEntities}
					value={legalEntityId}
					onChange={setLegalEntityId}
					disabled={saving}
				/>
			}
		>
			<PgPanel
				title="Fee rules: service × payment method"
				bodyPadding={1}
				actions={
					<>
						{(loading || saving) && <CircularProgress size={18} />}
						{changes.length > 0 && (
							<Typography variant="body2" color="primary">
								{changes.length} unsaved change{changes.length === 1 ? '' : 's'}
							</Typography>
						)}
						<Button size="small" onClick={handleDiscard} disabled={!changes.length || saving}>
							Discard
						</Button>
						<Button
							size="small"
							variant="contained"
							onClick={handleSave}
							disabled={!changes.length || hasErrors || saving}
						>
							Save
						</Button>
					</>
				}
			>
				{currentMatrix ? (
					<>
						<FeeMatrixTable
							matrix={currentMatrix}
							drafts={drafts}
							disabled={saving}
							onChange={handleCellChange}
						/>
						<Box sx={{ p: 1 }}>
							<Alert severity="info">
								Percent is a gross-up rate (3 % means the merchant receives the invoice amount after
								a 3 % provider fee). A removed rule that was already used by payments is deactivated
								instead of being deleted. Direct Debit is normally 0.
							</Alert>
						</Box>
					</>
				) : (
					<Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
						{loading || legalEntityId === null ? 'Loading…' : 'No data.'}
					</Typography>
				)}
			</PgPanel>
			{currentMatrix && (
				<FeeCalculator matrix={currentMatrix} drafts={drafts} hasChanges={changes.length > 0} />
			)}
		</PgPage>
	);
};
